import {
  DefaultInspirationPerPage,
  InspirationPublishedStatus,
} from "@/types/inspiration"
import {
  GetNotificationSettingsResponse,
  NotificationGroup,
  NotificationSetting,
  UpdateNotificationSettingsRequestBody,
} from "@/types/notification-settings"
import { InitProfileProps } from "@/types/profile"
import { SettingTabType } from "@/types/settings"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { CollectionsApi } from "@/lib/collections-api"
import { httpIsCancelled } from "@/lib/exception-utils"
import { InspirationApi } from "@/lib/inspiration-api"
import { NotificationSettingsApi } from "@/lib/notification-settings-api"
import profileSlice, {
  addUserProfilePaginatedCollections,
  addUserProfilePaginatedGuides,
  ProfileTabType,
  selectNotificationSettings,
  selectUserProfileCollectionsCount,
  selectUserProfilePaginatedCollections,
  selectUserProfilePaginatedGuides,
  selectUserProfileTab,
  selectUserSettingTab,
  setInitialized,
  setNotificationSettings,
  setNotificationSettingsError,
  setNotificationSettingsHasPendingChanges,
  setNotificationSettingsLoading,
  setNotificationSettingsResetting,
  setNotificationSettingsSaving,
  setUserProfileCurrCollectionsPage,
  setUserProfileCurrInspirationPage,
  setUserProfileLoadingCollections,
  setUserProfileLoadingGuides,
  setUserProfileMaxCollectionsPage,
  setUserProfileMaxInspirationPage,
  setUserProfilePaginatedCollections,
  setUserProfilePaginatedGuides,
  setUserProfileTab,
} from "@/lib/storage/profile-slice"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import { toastManager } from "@/lib/toast"
import { selectSavedGuidesCount } from "./api/saved-inspirations-api"
import userSlice from "./user-slice"

export const initProfile = createAsyncThunk<
  void,
  InitProfileProps,
  { dispatch: AppDispatch; state: RootState }
>("profile/initProfile", async (pageProps, { dispatch, getState }) => {
  dispatch(
    profileSlice.actions.setUserProfileGuidesCount(pageProps.guidesCount)
  )
  dispatch(
    profileSlice.actions.setUserProfileCollectionsCount(
      pageProps.collectionsCount
    )
  )
  dispatch(
    userSlice.actions.setFollowCounts({
      userRef: pageProps.initUser.user_ref,
      followers: pageProps.userProfile.followers_count || 0,
      following: pageProps.userProfile.following_count || 0,
    })
  )
  dispatch(userSlice.actions.setUserProfile(pageProps.userProfile))
  dispatch(setInitialized(true))

  const currentTab = selectUserProfileTab(getState())
  await dispatch(
    setTabWithData({
      tab: currentTab,
    })
  )

  sendAnalyticsEvent("ViewProfilePage")
})

let fetchInspirationsAbort: { abort: () => void } | undefined = undefined

export const fetchUserInspirations = createAsyncThunk<
  void,
  { page: number },
  { dispatch: AppDispatch; state: RootState }
>(
  "profile/fetchUserInspirations",
  async ({ page }, { dispatch, abort, signal }) => {
    fetchInspirationsAbort?.abort()
    fetchInspirationsAbort = { abort }

    try {
      dispatch(setUserProfileLoadingGuides(true))
      const inspirationApi = new InspirationApi(buildClientAxios())
      const inspirationsRet = await inspirationApi.fetchUserInspirations(
        {
          status: InspirationPublishedStatus,
          page: page,
          perPage: DefaultInspirationPerPage,
        },
        { signal }
      )

      if (page === 1) {
        dispatch(setUserProfilePaginatedGuides(inspirationsRet.data))
      } else {
        dispatch(addUserProfilePaginatedGuides(inspirationsRet.data))
      }

      dispatch(
        setUserProfileCurrInspirationPage(inspirationsRet.meta.current_page)
      )
      dispatch(
        setUserProfileMaxInspirationPage(inspirationsRet.meta.total_pages)
      )
    } catch (e) {
      if (!httpIsCancelled(e)) {
        console.error("Failed to fetch inspirations", e)
        toastManager.add({
          type: "error",
          description: "Sorry, we're having trouble loading your guides.",
        })
      }
    } finally {
      dispatch(setUserProfileLoadingGuides(false))
    }
  }
)

export const setTabWithData = createAsyncThunk<
  void,
  { tab: ProfileTabType },
  { dispatch: AppDispatch; state: RootState }
>("profile/setTabWithData", async ({ tab }, { dispatch, getState }) => {
  const state = getState()
  const currentTab = selectUserProfileTab(state)
  const guidesCount = selectSavedGuidesCount(state)
  const collectionsCount = selectUserProfileCollectionsCount(state)
  const guides = selectUserProfilePaginatedGuides(state)
  const collections = selectUserProfilePaginatedCollections(state)

  if (
    tab !== currentTab ||
    (tab === "guides" && (!guides || guidesCount !== guides.length)) ||
    (tab === "collections" &&
      (!collections || collectionsCount !== collections.length))
  ) {
    dispatch(setUserProfileTab(tab))

    if (tab === "guides") {
      await dispatch(loadGuidesIfNeeded())
    } else if (tab === "collections") {
      await dispatch(loadCollectionsIfNeeded())
    } else if (tab === "reviews") {
      // Reviews are loaded by RTK Query in the tab component.
    }
  }
})

export const loadGuidesIfNeeded = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("profile/loadGuidesIfNeeded", async (_, { dispatch }) => {
  try {
    dispatch(setUserProfileLoadingGuides(true))
    await dispatch(fetchUserInspirations({ page: 1 }))
  } finally {
    dispatch(setUserProfileLoadingGuides(false))
  }
})

export const loadCollectionsIfNeeded = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch }
>("profile/loadCollectionsIfNeeded", async (_, { dispatch }) => {
  try {
    dispatch(setUserProfileLoadingCollections(true))
    await dispatch(fetchUserCollections({ page: 1 }))
  } finally {
    dispatch(setUserProfileLoadingCollections(false))
  }
})

let fetchUserCollectionsAbort: { abort: () => void } | undefined = undefined
export const fetchUserCollections = createAsyncThunk<
  void,
  { page: number },
  { dispatch: AppDispatch; state: RootState }
>("profile/fetchUserCollections", async ({ page }, { dispatch, abort }) => {
  fetchUserCollectionsAbort?.abort()
  fetchUserCollectionsAbort = { abort }

  try {
    dispatch(setUserProfileLoadingCollections(true))
    const collectionsApi = new CollectionsApi(buildClientAxios())
    const ret = await collectionsApi.fetchCollections({
      isPublic: true,
      page,
    })

    if (page === 1) {
      dispatch(setUserProfilePaginatedCollections(ret.data))
    } else {
      dispatch(addUserProfilePaginatedCollections(ret.data))
    }

    dispatch(setUserProfileCurrCollectionsPage(ret.meta.current_page))
    dispatch(setUserProfileMaxCollectionsPage(ret.meta.total_pages))
  } catch (e) {
    if (!httpIsCancelled(e)) {
      console.error("Failed to fetch collections", e)
      toastManager.add({
        type: "error",
        description: "Sorry, we're having trouble loading your collections.",
      })
    }
  } finally {
    dispatch(setUserProfileLoadingCollections(false))
  }
})

// Notification Settings Thunks
export const fetchNotificationSettings = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("profile/fetchNotificationSettings", async (_, { dispatch }) => {
  try {
    dispatch(setNotificationSettingsLoading(true))
    dispatch(setNotificationSettingsError(null))

    const notificationSettingsApi = new NotificationSettingsApi(
      buildClientAxios()
    )
    const settings = await notificationSettingsApi.getNotificationSettings()

    dispatch(setNotificationSettings(settings))
  } catch (err) {
    const errorMessage = "Failed to load notification settings"
    dispatch(setNotificationSettingsError(errorMessage))
    console.error("Failed to fetch notification settings:", err)
  } finally {
    dispatch(setNotificationSettingsLoading(false))
  }
})

export const updateNotificationGroup = createAsyncThunk<
  void,
  { group: NotificationGroup; isEnabled: boolean },
  { dispatch: AppDispatch; state: RootState }
>(
  "profile/updateNotificationGroup",
  async ({ group, isEnabled }, { dispatch, getState }) => {
    const state = getState()
    const currentSettings = selectNotificationSettings(state)

    if (!currentSettings) return

    if (isEnabled) {
      // When enabling, use server-side reset to restore group to defaults
      try {
        const notificationSettingsApi = new NotificationSettingsApi(
          buildClientAxios()
        )
        const resetSettings =
          await notificationSettingsApi.resetNotificationSettingsToDefaults(
            group.key
          )

        dispatch(setNotificationSettings(resetSettings))
        dispatch(setNotificationSettingsHasPendingChanges(false))
      } catch (err) {
        console.error("Failed to reset notification group:", err)
        toastManager.add({
          type: "error",
          description: "Failed to enable notification group",
        })
      }
    } else {
      // When disabling, set all channels to false
      const updatedConfig = currentSettings.config.map((g) => {
        if (g.key === group.key) {
          return {
            ...g,
            items: g.items.map((setting) => ({
              ...setting,
              values: { email: false, push: false, sms: false },
            })),
          }
        }
        return g
      })

      const updatedSettings = {
        ...currentSettings,
        config: updatedConfig,
      }

      // Optimistically update the state
      dispatch(setNotificationSettings(updatedSettings))
      dispatch(setNotificationSettingsHasPendingChanges(true))

      // Send to server with debounce handled by the component
      void dispatch(saveNotificationSettings(updatedSettings))
    }

    sendAnalyticsEvent("NotificationGroupToggled")
  }
)

export const updateNotificationSetting = createAsyncThunk<
  void,
  { updatedSetting: NotificationSetting; groupKey: string },
  { dispatch: AppDispatch; state: RootState }
>(
  "profile/updateNotificationSetting",
  async ({ updatedSetting, groupKey }, { dispatch, getState }) => {
    const state = getState()
    const currentSettings = selectNotificationSettings(state)

    if (!currentSettings) return

    const updatedConfig = currentSettings.config.map((group) => {
      if (group.key === groupKey) {
        return {
          ...group,
          items: group.items.map((setting) =>
            setting.key === updatedSetting.key ? updatedSetting : setting
          ),
        }
      }
      return group
    })

    const updatedSettings = {
      ...currentSettings,
      config: updatedConfig,
    }

    // Optimistically update the state
    dispatch(setNotificationSettings(updatedSettings))
    dispatch(setNotificationSettingsHasPendingChanges(true))

    // Send to server with debounce handled by the component
    void dispatch(saveNotificationSettings(updatedSettings))

    sendAnalyticsEvent("NotificationSettingUpdated")
  }
)

export const saveNotificationSettings = createAsyncThunk<
  void,
  GetNotificationSettingsResponse,
  { dispatch: AppDispatch; state: RootState }
>("profile/saveNotificationSettings", async (settings, { dispatch }) => {
  try {
    dispatch(setNotificationSettingsSaving(true))

    const requestBody: UpdateNotificationSettingsRequestBody = {
      notification_settings: {
        disable_all: settings.disable_all,
        config: settings.config,
      },
    }

    const notificationSettingsApi = new NotificationSettingsApi(
      buildClientAxios()
    )
    const updatedSettings =
      await notificationSettingsApi.updateNotificationSettings(requestBody)

    // Update with server response
    dispatch(setNotificationSettings(updatedSettings))
    dispatch(setNotificationSettingsHasPendingChanges(false))
  } catch (err) {
    console.error("Failed to save notification settings:", err)
    toastManager.add({
      type: "error",
      description: "Failed to save notification settings",
    })
    // Refetch to get the correct state
    void dispatch(fetchNotificationSettings())
  } finally {
    dispatch(setNotificationSettingsSaving(false))
  }
})

export const resetNotificationSettingsToDefaults = createAsyncThunk<
  void,
  void,
  { dispatch: AppDispatch; state: RootState }
>("profile/resetNotificationSettingsToDefaults", async (_, { dispatch }) => {
  try {
    dispatch(setNotificationSettingsResetting(true))

    const notificationSettingsApi = new NotificationSettingsApi(
      buildClientAxios()
    )
    const resetSettings =
      await notificationSettingsApi.resetNotificationSettingsToDefaults()

    // Update with server response
    dispatch(setNotificationSettings(resetSettings))
    dispatch(setNotificationSettingsHasPendingChanges(false))
    toastManager.add({
      type: "success",
      description: "Notification settings reset to defaults",
    })
  } catch (err) {
    console.error("Failed to reset notification settings:", err)
    toastManager.add({
      type: "error",
      description: "Failed to reset notification settings",
    })
  } finally {
    dispatch(setNotificationSettingsResetting(false))
  }
})

// Settings Tab Management
export const changeSettingsTab = createAsyncThunk<
  void,
  { tab: SettingTabType | null },
  { dispatch: AppDispatch; state: RootState }
>("profile/changeSettingsTab", async ({ tab }, { dispatch, getState }) => {
  const state = getState()
  const currentTab = selectUserSettingTab(state)
  const notificationSettings = selectNotificationSettings(state)

  // Only proceed if tab is actually changing or data needs to be loaded
  if (
    tab !== currentTab ||
    (tab === "notifications" && !notificationSettings)
  ) {
    dispatch(profileSlice.actions.setUserSettingTab(tab))

    if (tab === "notifications" && !notificationSettings) {
      await dispatch(fetchNotificationSettings())
    }
  }
})
